import { Component } from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ActivatedRoute } from '@angular/router';
import { AppService } from '@App';
import { FeatureToggle } from '@Enums';
import { UserFormState } from '@Enums/global/userprofile-userform-state.enum';
import { UpdateUserAvailableDto } from '@Models';
import { FeatureManagerService, RoutesEnum, RoutingService, UserAdminService } from '@Services';
import { SubSink } from 'subsink';
import { MatDialog } from '@angular/material/dialog';
import { UnavailabilityScheduleComponent } from '../unavailability-schedule/unavailability-schedule.component';
import { UserAvailabilityService } from '@Services/user-availability.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-my-account',
  host: {'class': 'content'},
  templateUrl: './my-account.component.html',
  styleUrls: ['./my-account.component.scss']
})
export class MyAccountComponent {
  accounts: any[];
  clientAccountsDictionary: Record<string, any[]>;
  filteredAccounts: any[];
  selectedUser: any;

  private readonly subs = new SubSink();
  expandAll: boolean;
  filterText = '';
  isUserAvailable: any;
  userFormState: string;

  availability$!: Observable<boolean>;

  unavailabilityScheduleIsEnabled: boolean = false;

  constructor(
    private readonly userAdminService: UserAdminService, 
    private activatedRoute: ActivatedRoute, 
    private appService: AppService,
    private routingService: RoutingService,
    public dialog: MatDialog,
    private featureManagerService: FeatureManagerService,
    public availabilityService: UserAvailabilityService) {

  }

  ngOnInit() {
    this.subscribeToFeatureToggles();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  subscribeToFeatureToggles() {
    this.featureManagerService.isEnabled(FeatureToggle.GlobalEnableMyAccount).subscribe((result) => {
      if (!result) {
        this.routingService.navigateToRoute(RoutesEnum.dashboard);
      }
      else {
        this.getCurrentUser();
        this.setupClients();
      }
    });

    this.featureManagerService.isEnabled(FeatureToggle.GlobalEnableUnavailabilitySchedule).subscribe((result) => {
          if (result) {
            this.unavailabilityScheduleIsEnabled = true;
          }
    });
  }

  getCurrentUser() {
    const currentUserId = this.appService.getUserId();
    this.subs.add(this.userAdminService.getUser(currentUserId).subscribe(result => {
        this.selectedUser = result;
        this.getUserAvailability();
    }));
  }

  getUserAvailability() {
    this.availability$ = this.availabilityService.getAvailability(this.selectedUser.id);
    this.isUserAvailable = this.availabilityService.getCurrentAvailabilityByUser(this.selectedUser.id);
  }

  toggleUserAvailability(event: MatSlideToggleChange) {
    const isAvailable = event.checked;
    event.source.checked = !isAvailable;
    this.availabilityService.updateAvailability(this.selectedUser.id, event.checked, true).subscribe((response) => {
        if (response) {
          this.isUserAvailable = this.availabilityService.getCurrentAvailabilityByUser(this.selectedUser.id);
          this.userFormState = UserFormState.Saved;
          setTimeout(() => {
            this.userFormState = UserFormState.None;
          }, 1500);
        } else {
          event.source.checked = !isAvailable;
        }
      });
   }

   setupClients() {
          this.appService.getData('Accounts').subscribe((response) => {
            this.accounts = response.data;
            this.filteredAccounts = this.accounts.sort((a, b) => {
            const nameA = a.clientName ?? "";
            const nameB = b.clientName ?? "";
            return nameA.localeCompare(nameB);
          });
          this.generateClientAccountsDictionary();
    });  
  }

  generateClientAccountsDictionary() {
    const clientAccountsDictionary: Record<string, any[]> = this.filteredAccounts.reduce((dict, account) => {
      if (!dict[account.clientName]) {
        dict[account.clientName] = [];
      }
      dict[account.clientName].push(account);
      return dict;
    }, {} as Record<string, any[]>);

    for (const clientName in clientAccountsDictionary) {
      clientAccountsDictionary[clientName].sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
    }

    this.clientAccountsDictionary = clientAccountsDictionary;
  }

  get hasClientAccounts(): boolean {
    return !!this.clientAccountsDictionary &&
         Object.keys(this.clientAccountsDictionary).length > 0;
  }

  toggleExpandAll() {
    this.expandAll = !this.expandAll;
  }

  filterClients(entry: any) {
    const clients = this.selectedUser?.clients;
    return clients.filter(c => c.clientName === entry.key);
  }

  filterClientsAndAccountsByText() {
    this.filteredAccounts = this.accounts.filter(a => a.clientName?.toLowerCase().includes(this.filterText.toLowerCase()) ||
                                              a.code?.includes(this.filterText) ||
                                              a.name?.toLowerCase().includes(this.filterText.toLowerCase()));
    this.generateClientAccountsDictionary();

    if(this.filterText === ''){
      this.expandAll = false;
    }
    else {
      this.expandAll = true;
    }
  }

  openScheduleDialog() {
    this.isUserAvailable = this.availabilityService.getCurrentAvailabilityByUser(this.selectedUser.id);
    this.dialog.open(UnavailabilityScheduleComponent, {
            width: '900px',
            height: '700px',
            disableClose: true,
            data: {
                currentUser: this.selectedUser,
                userAvailable: this.isUserAvailable,
            }
        });
  }
}
